/**
 * Botão — nativo, e não pela ponte do `webhook-common`.
 *
 * ## Por que saiu da ponte
 *
 * O widget legado (`webhook-form-button`) **crava `nzType="primary"`** no
 * template e lê só `field.action.icon`, `field.isDisabled` e `field.size`. Ele
 * ignora o tipo do botão inteiro: configurar "Perigo" no inspector não mudava
 * nada na tela, porque não havia por onde a informação chegar.
 *
 * E ele não emite clique. `onClick` existe no modelo, aparece na aba
 * Comportamento, o runtime sabe executá-lo — e nunca disparava, porque a ponte
 * só devolve `change`. Configurar era possível; funcionar, não.
 *
 * Os dois somem aqui, e é o mesmo motivo: um botão é o componente mais
 * INTERATIVO da tela, e a ponte foi desenhada para campos de formulário.
 */
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  input
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormGroup } from '@angular/forms';
import { NzButtonModule, NzButtonType } from 'ng-zorro-antd/button';
import { NzIconModule } from 'ng-zorro-antd/icon';
import { TranslateService } from '@ngx-translate/core';

import { ScreenNode } from '../../model/node-props';
import { translateTAValue } from '../../../system-utils/pipes/translateTA';
import { NodeRenderer, RenderMode } from '../node-renderer';
import { SCREEN_RUNTIME } from '../../runtime/screen-runtime';

/**
 * O que o modelo chama de `variant`, traduzido para o NG-Zorro.
 *
 * "Perigo" não é um `nzType` — no Ant ele é um sinalizador à parte
 * (`nzDanger`), que se combina com qualquer tipo. Tratá-lo como tipo era o que
 * fazia a opção existir no inspector e não ter efeito.
 */
const NZ_TYPE: Readonly<Record<string, NzButtonType>> = {
  default: 'default',
  primary: 'primary',
  dashed: 'dashed',
  text: 'text',
  link: 'link',
  // Perigo desenha como primário, e o vermelho vem do `nzDanger`.
  danger: 'primary'
};

@Component({
  selector: 'tem-button-field',
  standalone: true,
  imports: [CommonModule, NzButtonModule, NzIconModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <button
      nz-button
      type="button"
      [nzType]="nzType()"
      [nzDanger]="isDanger()"
      [nzSize]="size()"
      [nzLoading]="loading()"
      [disabled]="disabled()"
      (click)="onClick()">

      @if (icon(); as name) {
        <nz-icon [nzType]="name" nzTheme="outline" />
      }

      {{ text() }}
    </button>
  `,
  styles: [`
    :host { display: block; }

    /* Ocupa a largura que o container deu. Um botão de 24 colunas que desenha
       com a largura do texto faz o usuário achar que a largura não funcionou. */
    button { width: 100%; }
  `]
})
export class ButtonFieldRenderer implements NodeRenderer<'button'> {

  readonly node = input.required<ScreenNode<'button'>>();
  readonly form = input<FormGroup | null>(null);
  readonly mode = input<RenderMode>('view');

  private readonly translate = inject(TranslateService);

  /**
   * O runtime, quando houver.
   *
   * Opcional porque o botão também aparece sem runtime — na prévia de um
   * editor, por exemplo. `null` ali é o caso normal, não uma falha.
   */
  private readonly runtime = inject(SCREEN_RUNTIME, { optional: true });

  private readonly props = computed(() => this.node().props);

  readonly text = computed<string>(() => {
    const chave = this.props().labelKey;

    return typeof chave === 'string' && chave.length > 0
      ? translateTAValue(this.translate, chave)
      : '';
  });

  readonly icon = computed<string>(() => this.props().icon ?? '');

  readonly nzType = computed<NzButtonType>(
    () => NZ_TYPE[this.props().variant ?? 'default'] ?? 'default'
  );

  readonly isDanger = computed<boolean>(() => this.props().variant === 'danger');

  readonly size = computed<'small' | 'default' | 'large'>(
    () => this.props().size ?? 'default'
  );

  readonly loading = computed<boolean>(() => this.props().loading === true);

  readonly disabled = computed<boolean>(() => this.props().disabled === true);

  /**
   * O clique vira gatilho `onClick` no runtime.
   *
   * **No editor não dispara.** Clicar num botão para selecioná-lo e configurar
   * a ação dele não pode executar a ação — senão configurar um botão que chama
   * um fluxo passa a chamar o fluxo a cada clique de seleção.
   */
  onClick(): void {
    if (this.mode() === 'editPreview') {
      return;
    }

    this.runtime?.trigger(this.node().id, 'onClick');
  }
}
